import { create } from 'zustand';
import { axiosApi } from '../../../axios-api';
import { appBackendApi } from '../../../app/appBackendApi';
import { message } from 'antd';
import { buildMappingProductFields } from '../utils/buildMappingProductFields';
import { buildFeedTemplateMappings, pickDefaultTemplate } from '../utils/buildFeedTemplateMappings';
import {
  changeFieldsIdWithDisplayName,
  changeFieldsDisplayNameWithId,
} from '../services/ValidateService';

const initialState = {
  name: null,
  project: null,
  ruleId: null,
  productFields: null,
  mappings: [],
  feedTypes: [],
  formatTypes: [],
  formatType: null,
  feedTemplate: null,
  selectedFeedTypeId: null,
  mappingId: null,
  projectId: null,
};


const useMappingStore = create((set, get) => ({
  ...initialState,

  reset: () => set({ ...initialState }),

  setName: (name) => set({ name }),

  setMappings: (mappings) => set({ mappings }),


  syncFeedTemplate: (overrides = {}) => {
    const state = { ...get(), ...overrides };
    const { selectedFeedTypeId, feedTypes, mappingId, productFields } = state;
    if (!selectedFeedTypeId || !Array.isArray(feedTypes) || feedTypes.length === 0) {
      if (Object.keys(overrides).length) set(overrides);
      return;
    }
    const feed = feedTypes.find((t) => t.id === selectedFeedTypeId);
    if (!feed) {
      set({
        ...overrides,
        feedTemplate: null,
        formatTypes: [],
        formatType: state.formatType ?? null,
      });
      return;
    }
    const formatTypes = feed.formatTypes ?? [];
    const next = {
      ...overrides,
      feedTemplate: feed,
      formatTypes,
      formatType: state.formatType ?? formatTypes[0] ?? null,
    };
    if (!mappingId) {
      next.mappings = buildFeedTemplateMappings(feed, productFields ?? []);
    }
    set(next);
  },

  setFeedTypeId: (selectedFeedTypeId) => {
    get().syncFeedTemplate({ selectedFeedTypeId, formatType: null });
  },

  setFormatType: (formatType) => set({ formatType }),


  addMapping: () =>
    set((s) => ({
      mappings: [...s.mappings, { feedFieldId: '', value: '', error: false }],
    })),


  removeMapping: (mapping) =>
    set((s) => ({ mappings: s.mappings.filter((m) => m !== mapping) })),


  updateMapping: (index, field, value) =>
    set((s) => {
      const updated = [...s.mappings];
      updated[index] = { ...updated[index], [field]: value };
      return { mappings: updated };
    }),


  addParamAttribute: (attributeName) =>
    set((state) => ({
      feedTemplate: {
        ...state.feedTemplate,
        paramAttributes: [
          ...(state.feedTemplate?.paramAttributes ?? []),
          attributeName,
        ],
      },
    })),


  init: async (projectId, mappingId, callbacks = {}) => {
    set({ projectId, mappingId });
    await Promise.all([
      get().loadFeedTypes(),
      get().loadProject(projectId, callbacks.onProjectLoadError),
    ]);
    if (mappingId) await get().loadMappingData(mappingId);
  },


  loadProject: async (projectId, onProjectLoadError) => {
    try {
      const data = await appBackendApi.fetchProject(projectId);
      const { formatType } = get();
      const isXml = formatType === 'yml' || formatType === 'xml';
      set({
        project: data,
        ruleId: data?.rule?.id,
        productFields: buildMappingProductFields(data, isXml),
      });
      if (get().selectedFeedTypeId && !get().mappingId) {
        get().syncFeedTemplate();
      }
    } catch (error) {
      message.error(error.message);
      if (onProjectLoadError) onProjectLoadError();
    }
  },

  
  loadFeedTypes: async () => {
    try {
      const data = await appBackendApi.fetchFeedTypes();
      set({ feedTypes: data });
      const { selectedFeedTypeId, mappingId } = get();
      if (selectedFeedTypeId) {
        get().syncFeedTemplate();
      } else if (!mappingId && Array.isArray(data) && data.length > 0) {
        get().setFeedTypeId(data[0].id);
      }
    } catch (error) {
      message.error(error.message);
    }
  },


  loadMappingData: async (mappingId) => {
    const { projectId, feedTypes, productFields } = get();
    try {
      const data = await appBackendApi.fetchMapping(projectId, mappingId);
      if (!data) return;

      const mappingJson = data.mapping_json ? JSON.parse(data.mapping_json) : {};
      const feed = Array.isArray(feedTypes)
        ? feedTypes.find((t) => t.id === data.preset_name)
        : null;
      const pf = productFields ?? [];

      const fields = [];
      Object.entries(mappingJson).forEach(([key, rawValue]) => {
        if (key === 'param' && rawValue && typeof rawValue === 'object') {
          Object.entries(rawValue).forEach(([attribute, value]) => {
            fields.push({
              feedFieldId: 'param',
              value,
              attribute,
              required: false,
              readonly: false,
              error: '',
              type: 'param',
            });
          });
          return;
        }

        const feedField = feed?.fields?.find((f) => f.name === key);
        const defaultValues = feedField?.default ?? [];
        const isMultiUseArray = feedField?.multiUse && Array.isArray(rawValue);

        if (isMultiUseArray) {
          rawValue.forEach((v, i) => {
            fields.push({
              feedFieldId: key,
              value: changeFieldsIdWithDisplayName(v ?? '', pf),
              required: Boolean(feedField?.required) && i === 0,
              readonly:
                Boolean(feedField?.readonly) &&
                defaultValues.map((d) => `{{${d}}}`).includes(v),
              error: '',
              type: 'field',
            });
          });
        } else {
          fields.push({
            feedFieldId: key,
            value: changeFieldsIdWithDisplayName(rawValue ?? '', pf),
            required: Boolean(feedField?.required),
            readonly:
              Boolean(feedField?.readonly) &&
              defaultValues.map((d) => `{{${d}}}`).includes(rawValue),
            error: '',
            type: 'field',
            hidden: Boolean(feedField?.hidden),
          });
        }
      });

      const presentFieldIds = new Set(
        fields.filter((f) => f.type === 'field').map((f) => f.feedFieldId),
      );
      (feed?.fields ?? []).forEach((ff) => {
        if (ff.required !== true || presentFieldIds.has(ff.name)) return;
        const tpl = pickDefaultTemplate(ff.defaultTemplate, pf);
        if (!tpl) return;
        fields.push({
          feedFieldId: ff.name,
          value: changeFieldsIdWithDisplayName(tpl, pf),
          required: true,
          readonly: Boolean(ff.readonly),
          error: '',
          type: 'field',
          hidden: Boolean(ff.hidden),
        });
      });

      set({
        name: data.name,
        selectedFeedTypeId: data.preset_name,
        formatType: data.format_type,
        mappings: fields,
      });
      get().syncFeedTemplate();
    } catch (error) {
      message.error(error.message);
    }
  },


  saveMappings: async (onSaveSuccess) => {
    const {
      name,
      selectedFeedTypeId,
      mappings,
      formatType,
      projectId,
      mappingId,
      feedTemplate,
      productFields,
    } = get();

    if (!name) {
      message.error('Enter feed name!');
      return;
    }

    if (!selectedFeedTypeId) {
      message.error('Enter feed template!');
      return;
    }

    const pf = productFields ?? [];
    const isCustomCsv =
      selectedFeedTypeId === 'custom_csv' ||
      Boolean(feedTemplate && feedTemplate.fields && feedTemplate.fields.length === 0);

    const mappingJson = {};
    mappings.forEach((m) => {
      if (m.type === 'param') {
        if (m.attribute && m.value !== '' && m.value != null) {
          if (!mappingJson.param) mappingJson.param = {};
          mappingJson.param[m.attribute] = m.value;
        }
        return;
      }
      if (!m.feedFieldId) return;

      if (isCustomCsv) {
        mappingJson[m.feedFieldId] = changeFieldsDisplayNameWithId(m.value, pf);
        return;
      }

      const feedField = feedTemplate?.fields?.find((f) => f.name === m.feedFieldId);
      const serialized = changeFieldsDisplayNameWithId(m.value, pf);

      if (feedField?.multiUse) {
        if (!Array.isArray(mappingJson[m.feedFieldId])) mappingJson[m.feedFieldId] = [];
        mappingJson[m.feedFieldId].push(serialized);
      } else {
        mappingJson[m.feedFieldId] = serialized;
      }
    });

    try {
      const url = `/projects/${projectId}/mappings/${mappingId ? `${mappingId}/` : ''}`;
      const method = mappingId ? 'PUT' : 'POST';

      const { data } = await axiosApi({
        method,
        url,
        data: {
          name,
          preset_name: selectedFeedTypeId,
          format_type: formatType ?? 'csv',
          mapping_json: JSON.stringify(mappingJson),
        },
      });

      message.success('Saved!');
      if (onSaveSuccess) onSaveSuccess(data.id);
    } catch (error) {
      message.error(error.message);
    }
  },
}));

export default useMappingStore;
